import { createSelector } from 'reselect';

import {
  DaiCostsField,
  DaiCostsSearchParams,
} from '../../domain/daiCosts/types';
import { OrderType } from '../../domain/schemas';

/* ---------------------------------------------------------------
 * Action Types
 */

const INIT_DAI_COSTS_SETTING = 'INIT_DAI_COSTS_SETTING' as const;
const SEARCH_DAI_COSTS_SETTING = 'SEARCH_DAI_COSTS_SETTING' as const;
const RENEW_DAI_COSTS_SETTING = 'RENEW_DAI_COSTS_SETTING' as const;
const TRIGGER_DAI_COSTS_SETTING_SORT =
  'TRIGGER_DAI_COSTS_SETTING_SORT' as const;
const SELECT_DAI_COSTS_SETTING_FORM_CONDITION =
  'SELECT_DAI_COSTS_SETTING_FORM_CONDITION' as const;
const SELECT_DAI_COSTS_SETTING_NAME_FILTER =
  'SELECT_DAI_COSTS_SETTING_NAME_FILTER' as const;
const SAVE_DAI_COSTS_SETTING_END_DATE =
  'SAVE_DAI_COSTS_SETTING_END_DATE' as const;
const SELECT_DAI_COSTS_KONYU_KBN_FILTER =
  'SELECT_DAI_COSTS_KONYU_KBN_FILTER' as const;
const SELECT_DAI_COSTS_INITIAL_HALL_MULTI_SELECT =
  'SELECT_DAI_COSTS_INITIAL_HALL_MULTI_SELECT' as const;
const SELECT_DAI_COSTS_CURRENT_HALL_MULTI_SELECT =
  'SELECT_DAI_COSTS_CURRENT_HALL_MULTI_SELECT' as const;

export const DaiCostsSettingActionTypes = {
  INIT_DAI_COSTS_SETTING,
  SEARCH_DAI_COSTS_SETTING,
  RENEW_DAI_COSTS_SETTING,
  TRIGGER_DAI_COSTS_SETTING_SORT,
  SELECT_DAI_COSTS_SETTING_FORM_CONDITION,
  SELECT_DAI_COSTS_SETTING_NAME_FILTER,
  SAVE_DAI_COSTS_SETTING_END_DATE,
  SELECT_DAI_COSTS_KONYU_KBN_FILTER,
  SELECT_DAI_COSTS_INITIAL_HALL_MULTI_SELECT,
  SELECT_DAI_COSTS_CURRENT_HALL_MULTI_SELECT,
};

/* ---------------------------------------------------------------
 * Action Creators
 */

/**
 * 初回リクエスト
 */
function initDaiCostsSettingAction() {
  return {
    type: INIT_DAI_COSTS_SETTING,
  };
}

/**
 * 検索ボタンの押下
 */
function searchDaiCostsSettingAction(searchParams: DaiCostsSearchParams) {
  return {
    type: SEARCH_DAI_COSTS_SETTING,
    payload: { searchParams },
  };
}

function renewDaiCostsSettingAction() {
  return {
    type: RENEW_DAI_COSTS_SETTING,
  };
}

/**
 * ソートボタン押下 (サブメニュー内・ラベル共通)
 */
function triggerDaiCostsSettingSortAction(
  sort: DaiCostsField,
  order: OrderType
) {
  return {
    type: TRIGGER_DAI_COSTS_SETTING_SORT,
    payload: { sort, order },
  };
}

/**
 * 検索フォーム条件を変更（初回レスポンスの setting 反映用）
 */
function selectDaiCostsSettingFormCondition(
  formCondition: DaiCostsSearchParams
) {
  return {
    type: SELECT_DAI_COSTS_SETTING_FORM_CONDITION,
    payload: { formCondition },
  };
}

/**
 * 機種名フィルタを変更
 */
function selectDaiCostsSettingNameFilterAction(name: string) {
  return {
    type: SELECT_DAI_COSTS_SETTING_NAME_FILTER,
    payload: { name },
  };
}

/**
 * 選択可能な最大未来日を保存
 */
function saveDaiCostsSettingEndDateAction(endDate: string) {
  return {
    type: SAVE_DAI_COSTS_SETTING_END_DATE,
    payload: { endDate },
  };
}

/**
 * 購入区分の絞り込みを変更（空文字は「選択なし」＝全件）
 */
function selectDaiCostsKonyuKbnFilterAction(filter: string) {
  return {
    type: SELECT_DAI_COSTS_KONYU_KBN_FILTER,
    payload: { filter },
  };
}

/**
 * 初期導入店舗の複数選択フィルタ（テーブルデータ由来の選択肢用）
 */
function selectDaiCostsInitialHallMultiSelectAction(selected: string[]) {
  return {
    type: SELECT_DAI_COSTS_INITIAL_HALL_MULTI_SELECT,
    payload: { selected },
  };
}

/**
 * 現設置店舗の複数選択フィルタ（テーブルデータ由来の選択肢用）
 */
function selectDaiCostsCurrentHallMultiSelectAction(selected: string[]) {
  return {
    type: SELECT_DAI_COSTS_CURRENT_HALL_MULTI_SELECT,
    payload: { selected },
  };
}

export const DaiCostsSettingActionCreators = {
  initDaiCostsSettingAction,
  searchDaiCostsSettingAction,
  renewDaiCostsSettingAction,
  triggerDaiCostsSettingSortAction,
  selectDaiCostsSettingFormCondition,
  selectDaiCostsSettingNameFilterAction,
  saveDaiCostsSettingEndDateAction,
  selectDaiCostsKonyuKbnFilterAction,
  selectDaiCostsInitialHallMultiSelectAction,
  selectDaiCostsCurrentHallMultiSelectAction,
};

/* ---------------------------------------------------------------
 * Actions
 */

type InitSettingDaiCostsAction = ReturnType<typeof initDaiCostsSettingAction>;
export type SearchSettingDaiCostsAction = ReturnType<
  typeof searchDaiCostsSettingAction
>;

type RenewDaiCostsSettingAction = ReturnType<typeof renewDaiCostsSettingAction>;

export type TriggerDaiCostsSettingSortAction = ReturnType<
  typeof triggerDaiCostsSettingSortAction
>;

type SelectDaiCostsSettingFormConditionAction = ReturnType<
  typeof selectDaiCostsSettingFormCondition
>;

type SelectDaiCostsSettingNameFilterAction = ReturnType<
  typeof selectDaiCostsSettingNameFilterAction
>;

type SaveDaiCostsSettingEndDateAction = ReturnType<
  typeof saveDaiCostsSettingEndDateAction
>;

type SelectDaiCostsKonyuKbnFilterAction = ReturnType<
  typeof selectDaiCostsKonyuKbnFilterAction
>;

type SelectDaiCostsInitialHallMultiSelectAction = ReturnType<
  typeof selectDaiCostsInitialHallMultiSelectAction
>;

type SelectDaiCostsCurrentHallMultiSelectAction = ReturnType<
  typeof selectDaiCostsCurrentHallMultiSelectAction
>;

type DaiCostsSettingAction =
  | InitSettingDaiCostsAction
  | SearchSettingDaiCostsAction
  | RenewDaiCostsSettingAction
  | TriggerDaiCostsSettingSortAction
  | SelectDaiCostsSettingFormConditionAction
  | SelectDaiCostsSettingNameFilterAction
  | SaveDaiCostsSettingEndDateAction
  | SelectDaiCostsKonyuKbnFilterAction
  | SelectDaiCostsInitialHallMultiSelectAction
  | SelectDaiCostsCurrentHallMultiSelectAction;

/* ---------------------------------------------------------------
 * State
 */

export type DaiCostsSettingState = {
  searchParams: DaiCostsSearchParams | undefined;
  /**
   * 機種名フィルタ
   */
  nameFilter: string | undefined;
  /**
   * 選択可能な最大未来日
   */
  selectableEndDate: string | undefined;
  /**
   * 購入区分の絞り込み（空文字は全件）。機種タグ設定の pg フィルタと同様
   */
  konyuKbnFilter: string;
  /**
   * 初期導入店舗の複数選択（空配列は絞り込みなし）
   */
  initialHallNameMultiSelect: string[];
  /**
   * 現設置店舗の複数選択（空配列は絞り込みなし）
   */
  currentHallNameMultiSelect: string[];
};

const initialState: DaiCostsSettingState = {
  searchParams: undefined,
  nameFilter: undefined,
  selectableEndDate: undefined,
  konyuKbnFilter: '',
  initialHallNameMultiSelect: [],
  currentHallNameMultiSelect: [],
};

/* ---------------------------------------------------------------
 * Selector
 */
/**
 * [機械代金登録] 設定情報を取得する
 */
function daiCostsSettingSelector(rootState: {
  daiCostsSetting: DaiCostsSettingState;
}) {
  return rootState.daiCostsSetting;
}

/**
 * [機械代金登録] 選択されている検索条件を取得する
 */
export const daiCostsSettingSearchParamsSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.searchParams
);

/**
 * [機械代金登録] 選択可能な最大未来日を取得する
 */
export const daiCostsSelectableEndDateSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.selectableEndDate
);

/**
 * 機械代金登録 機種名フィルターされている機種名を取得する
 */
export const daiCostsSettingNameFilterSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.nameFilter
);

/**
 * 機械代金登録 購入区分フィルタを取得する
 */
export const daiCostsKonyuKbnFilterSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.konyuKbnFilter
);

/**
 * 機械代金登録 初期導入店舗の複数選択フィルタを取得する
 */
export const daiCostsInitialHallNameMultiSelectSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.initialHallNameMultiSelect
);

/**
 * 機械代金登録 現設置店舗の複数選択フィルタを取得する
 */
export const daiCostsCurrentHallNameMultiSelectSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.currentHallNameMultiSelect
);

/**
 * 機械代金登録 ソート条件を取得する
 */
export const daiCostsSortSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.searchParams?.sort
);

/**
 * 機械代金登録 ソート順を取得する
 */
export const daiCostsOrderSelector = createSelector(
  daiCostsSettingSelector,
  (daiCostsSetting) => daiCostsSetting.searchParams?.order
);

/* ---------------------------------------------------------------
 * Reducer
 */

export function daiCostsSettingReducer(
  state = initialState,
  action: DaiCostsSettingAction
): DaiCostsSettingState {
  switch (action.type) {
    case SEARCH_DAI_COSTS_SETTING:
      // 導入日などを変えて再検索したとき、前回のクライアント側フィルタが残ると
      // 新しいデータに存在しない店舗名が選ばれたままになり表が空に見えるためリセットする
      return {
        ...state,
        searchParams: {
          ...action.payload.searchParams,
          ...(state.searchParams?.sort != null &&
          state.searchParams.order != null
            ? {
                sort: state.searchParams.sort,
                order: state.searchParams.order,
              }
            : {}),
        },
        nameFilter: undefined,
        initialHallNameMultiSelect: [],
        currentHallNameMultiSelect: [],
        konyuKbnFilter: '',
      };
    case RENEW_DAI_COSTS_SETTING:
      return initialState;
    case TRIGGER_DAI_COSTS_SETTING_SORT:
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload.sort,
          order: action.payload.order,
        },
      };
    case SELECT_DAI_COSTS_SETTING_FORM_CONDITION:
      return {
        ...state,
        searchParams: action.payload.formCondition,
      };
    case SELECT_DAI_COSTS_SETTING_NAME_FILTER:
      return {
        ...state,
        nameFilter: action.payload.name,
      };
    case SAVE_DAI_COSTS_SETTING_END_DATE:
      return {
        ...state,
        selectableEndDate: action.payload.endDate,
      };
    case SELECT_DAI_COSTS_KONYU_KBN_FILTER:
      return {
        ...state,
        konyuKbnFilter: action.payload.filter,
      };
    case SELECT_DAI_COSTS_INITIAL_HALL_MULTI_SELECT:
      return {
        ...state,
        initialHallNameMultiSelect: action.payload.selected,
      };
    case SELECT_DAI_COSTS_CURRENT_HALL_MULTI_SELECT:
      return {
        ...state,
        currentHallNameMultiSelect: action.payload.selected,
      };
    default:
      return state;
  }
}
